import { CCEntityConfig, CCEntityType, validateFlowType } from "components/CC/types/entityConfig";
import { remapReferenceTagsDeep } from "components/CC/utils/remapReferenceTags";

import type {
    DiagramEdge,
    DiagramNode,
} from "components/CC/Diagrams/ConnectionCreatorDiagram/ConnectionCreatorDiagram";

export interface BlueprintData {
    nodes: DiagramNode[];
    edges: DiagramEdge[];
}

export interface AdaptationWarning {
    key: string;
    params?: Record<string, unknown>;
}

export interface AdaptationResult {
    blueprint: BlueprintData;
    warnings: AdaptationWarning[];
}

export interface DuplicateConnection {
    source: string;
    target: string;
}

/**
 * Finds the first connection (edge) that shares its exact source and target with an earlier
 * edge — i.e. the same directed step-to-step connection defined twice. The reverse direction
 * (target -> source) and connections to a different step are not duplicates.
 */
export function findDuplicateConnection(edges: DiagramEdge[]): DuplicateConnection | null {
    const seen = new Set<string>();
    for (const edge of edges) {
        // Tuple encoding, not string concatenation — an id containing "->" would otherwise
        // collide with a different source/target pair (e.g. ("a", "b->c") vs ("a->b", "c")).
        const key = JSON.stringify([edge.source, edge.target]);
        if (seen.has(key)) {
            return { source: edge.source, target: edge.target };
        }
        seen.add(key);
    }
    return null;
}

type AdaptationStep = (data: BlueprintData, entityType: CCEntityType, config: CCEntityConfig) => AdaptationResult;

const adaptStartNodes: AdaptationStep = (data, entityType, config) => {
    const warnings: AdaptationWarning[] = [];
    const startNodes = data.nodes.filter((n) => n.type === "startNode");
    const nonStartNodes = data.nodes.filter((n) => n.type !== "startNode");

    if (startNodes.length === 0) {
        return { blueprint: data, warnings };
    }

    const keptStartNodes = startNodes.slice(0, config.maxStarts);
    const droppedStartNodes = startNodes.slice(config.maxStarts);

    if (droppedStartNodes.length > 0) {
        warnings.push({ key: "adapt_starts_dropped", params: { count: droppedStartNodes.length } });
    }

    const adaptedStartNodes = keptStartNodes.map((node) => {
        const formData = (node.data?.formData as Record<string, unknown>) ?? {};
        return {
            ...node,
            data: {
                ...node.data,
                formData: {
                    ...formData,
                    beType: config.startBeType,
                    flow: validateFlowType(entityType, formData.flow as string | undefined),
                },
            },
        };
    });

    const droppedIds = new Set(droppedStartNodes.map((n) => n.id));

    return {
        blueprint: {
            nodes: [...adaptedStartNodes, ...nonStartNodes],
            edges: data.edges.filter((e) => !droppedIds.has(e.source)),
        },
        warnings,
    };
};

const dropDanglingEdges: AdaptationStep = (data) => {
    const nodeIds = new Set(data.nodes.map((n) => n.id));
    const validEdges = data.edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
    return { blueprint: { ...data, edges: validEdges }, warnings: [] };
};

/**
 * Builds a map from a node's persisted backend id (beId) to its current flow-position id.
 * Previous-step reference tags (e.g. "{<beId>.status}") are generated against beId while a
 * diagram is BE-synced, so this map is needed to keep those tags valid once beId is stripped.
 */
const buildBeIdToNodeIdMap = (nodes: DiagramNode[]): Map<string, string> => {
    const map = new Map<string, string>();
    nodes.forEach((node) => {
        const beId = (node.data as Record<string, unknown> | undefined)?.beId as string | undefined;
        if (beId) {
            map.set(beId, node.id);
        }
    });
    return map;
};

/**
 * Rewrites "{<beId>.path}" previous-step reference tags to "{<node.id>.path}" so they stay
 * valid once beId (only meaningful for a BE-synced diagram) is stripped from exported/pasted
 * blueprints. Must run before beId is removed from node.data.
 */
export const remapStepReferences = (data: BlueprintData): BlueprintData => {
    const idMap = buildBeIdToNodeIdMap(data.nodes);
    if (idMap.size === 0) {
        return data;
    }

    return {
        nodes: data.nodes.map((node) => ({
            ...node,
            data: remapReferenceTagsDeep(node.data, idMap) as DiagramNode["data"],
        })),
        edges: data.edges.map((edge) => ({
            ...edge,
            data: remapReferenceTagsDeep(edge.data, idMap) as DiagramEdge["data"],
        })),
    };
};

// Pipeline of adaptation steps — add future steps here (e.g. adaptForbiddenNodeTypes)
const ADAPTATION_STEPS: AdaptationStep[] = [adaptStartNodes, dropDanglingEdges];

export function adaptBlueprintForEntity(
    data: BlueprintData,
    entityType: CCEntityType,
    config: CCEntityConfig
): AdaptationResult {
    let current = data;
    const allWarnings: AdaptationWarning[] = [];

    for (const step of ADAPTATION_STEPS) {
        const result = step(current, entityType, config);
        current = result.blueprint;
        allWarnings.push(...result.warnings);
    }

    return { blueprint: current, warnings: allWarnings };
}
